import { h } from 'preact';
import * as yup from 'yup';
import type { ValidationError } from 'yup';
import { throttle } from 'lodash';
import {
	useContext, useReducer, createContext, useEffect, useState, useMemo, useRef,
} from 'preact/compat';
import { Theme } from '../lib/render';

/**
 * Yup vaildation schema.
 */
const userSchema = yup.object().shape({
	FirstName: yup.string().required('First name is required'),
	LastName: yup.string().required('Last name is required'),
	EmailAddress: yup.string()
		.email('Email is invalid')
		.required('Email is required'),
	Occupation: yup.string().required('Occupation is required'),
	Employer: yup.string().required('Employer is required'),
});

const cagingUserSchema = yup.object().shape({
	FirstName: yup.string().required('First name is required'),
	LastName: yup.string().required('Last name is required'),
	EmailAddress: yup.string().email('Email is invalid'),
	Occupation: yup.string(),
	Employer: yup.string(),
});

/**
 * Yup address validation schema.
 */
const addressSchema = yup.object().shape({
	AddressLine1: yup.string().required('Address line one is required'),
	AddressLine2: yup.string(),
	City: yup.string().required('City is required'),
	StateProvince: yup.string().required('State is required'),
	Country: yup.string().required('Country is required'),
	PostalCode: yup.string().required('Zip is required'),
});

const addressSchemaFields = Object.keys(addressSchema.fields);
const personalSchemaFields = Object.keys(userSchema.fields);
const combinedSchema = userSchema.concat(addressSchema);
const cagingCombinedSchema = cagingUserSchema.concat(addressSchema);

/**
 * User Context.
 */
interface UserObj {
	FirstName?: string;
	LastName?: string;
	EmailAddress?: string;
	AddressLine1?: string;
	AddressLine2?: string;
	City?: string;
	StateProvince?: string;
	PostalCode?: string;
	Country?: string;
	Employer?: string;
	Occupation?: string;
	cardType?: string;
	last4?: string;
	sourceCodeName?: string;
	sourceCodeId?: number;
}

type UserActionPayload = {
	type: 'UPDATE_USER';
	payload: UserObj;
} | {
	type: 'CLEAR_USER';
}

type UserAction = (payload: UserActionPayload) => void;

/**
 *  User Reducer Actions.
 */
const userReducer = (state: UserObj, action: UserActionPayload) => {
	switch (action.type) {
	case 'UPDATE_USER':
		return { ...state, ...action.payload };
	case 'CLEAR_USER':
		return {};
	default:
		return state;
	}
};

type TouchedActionPayload = {
	type: 'TOUCH_FIELD';
	payload: string | string[];
} | {
	type: 'CLEAR_TOUCHED';
} | {
	type: 'TOUCH_ALL';
};

const touchedReducer = (state: string[], action: TouchedActionPayload): string[] | [] => {
	switch (action.type) {
	case 'TOUCH_FIELD':
		// Ensure always array.
		if (action.payload instanceof Array) {
			return [
				...state,
				...action.payload.filter((t: string) => !state.includes(t)),
			];
		}
		// Handle string to array.
		if (typeof action.payload === 'string') {
			return [
				...state,
				...[action.payload].filter((t: string) => !state.includes(t)),
			];
		}
		return state;
	case 'TOUCH_ALL':
		return [
			...(Object.keys(combinedSchema.fields)),
		];
	case 'CLEAR_TOUCHED':
		return [];
	default:
		return state;
	}
};

export const User = createContext<{
	user: UserObj,
	validUser: boolean,
	personalValid: boolean,
	addressValid: boolean,
	errors: { [key: string]: string },
	setErrors:(errors: { [key: string]: string }) => void,
	touched: string[],
	dispatch: UserAction,
	dispatchTouch: (payload: TouchedActionPayload) => void
		} | undefined>(undefined);

/**
 * User Provider.
 */
const UserProvider = ({ children }: { children: h.JSX.Element }) => {
	const theme = useContext(Theme);
	const [user, dispatch] = useReducer(userReducer, {});
	const [valid, setValid] = useState(false);
	const [errors, setErrors] = useState({});
	const [personalValid, setPersonalValid] = useState(false);
	const [addressValid, setAddressValid] = useState(false);
	const [touched, dispatchTouch] = useReducer(touchedReducer, []);

	// Validate user data.
	const initValid = useRef(false);
	const validateUser = useMemo(() => throttle((u: UserObj) => {
		/**
		 * Caging forms have specifc validation controls.
		 * If the theme is caging, use the caging schema.
		 */
		const schema = theme.donate_type === 'caging' ? cagingCombinedSchema : combinedSchema;
		schema.validate(u, { abortEarly: false }).then(() => {
			setValid(true);
			setErrors({});
		})
			.catch((err: ValidationError) => {
				const newErrors: {
					[key: string]: string
				} = {};
				err.inner.forEach((e: ValidationError) => {
					newErrors[e.path] = e.message;
				});
				setErrors(newErrors);
				setValid(false);
				initValid.current = true;
			});
	}, 100), [initValid, setValid, setErrors, theme]);

	// Validate on user data change.
	useEffect(() => {
		validateUser(user);
	}, [user]);

	/**
	 * Handle validating user & adddress state.
	 */
	useEffect(() => {
		/**
		 * Prevent validation until validateUser() has resolved.
		 */
		if (!initValid.current) {
			return;
		}

		/**
		 * Invalidate address.
		 */
		if (Object.keys(errors).filter((e) => addressSchemaFields.includes(e)).length > 0) {
			if (addressValid) {
				setAddressValid(false);
			}
		} else if (!addressValid) {
			setAddressValid(true);
		}

		/**
		 * Invalidate user.
		 */
		if (Object.keys(errors).filter((e) => personalSchemaFields.includes(e)).length > 0) {
			if (personalValid) {
				setPersonalValid(false);
			}
		} else if (!personalValid) {
			setPersonalValid(true);
		}
	}, [errors, personalValid, addressValid, initValid]);

	/**
	 * Set touched on user change.
	 * If value exists and not already touched.
	 */
	useEffect(() => {
		const n = Object.keys(user).filter((uV: string) => !touched.includes(uV as never));
		dispatchTouch({
			type: 'TOUCH_FIELD',
			payload: n,
		});
	}, [user]);

	const userContext = useMemo(() => ({
		user,
		dispatch,
		errors,
		setErrors,
		touched,
		dispatchTouch,
		validUser: valid,
		personalValid,
		addressValid,
	}), [user, errors, touched, valid, personalValid, addressValid]);

	return (
		<User.Provider value={userContext}>
			{children}
		</User.Provider>
	);
};

const useUser = () => {
	const context = useContext(User);
	if (context === undefined) {
		throw new Error('useUser must be used within a UserProvider');
	}
	return context;
};

export { UserProvider, useUser };
